/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import IcomoonReact from 'icomoon-react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import Select from 'react-select';
import { toast } from 'react-toastify';
import TBDatePicker from '../../../shared/components/TBDatePicker/TBDatePicker';

import iconSet from '../../../shared/images/teambuildr-selection.json';
import {
  convertRecurringItem,
  updateRecurringItemSchedule,
} from '../ducks/calendarActions';

const ModalContainer = styled('div')`
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
`;

const ModalTextTop = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 24px;
  font-weight: 700;
`;

const Subtext = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 400;
  margin-top: 5px;
  color: #9E9E9E;
`;

const Subtext2 = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 700;
  margin-top: 15px;
  color: black;
`;

const TextContainer = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const ButtonsContainer = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
  flex-shrink: 0;
  margin-top: 30px;
`;

const TopTextAndX = styled('div')`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const SchedulingOptionsStack = styled('div')`
  display: flex;
  flex-direction: column;
`;

const TopSelectRow = styled('div')`
  display: flex;
  justify-content: space-between;
  margin-top: 3px;
`;

const DatePickerStack = styled('div')`
  display: flex;
  flex-direction: column;
`;

const DatePickerRow = styled('div')`
  display: flex;
  justify-content: space-between;
  margin-top: 3px;
`;

const PickerAndText = styled('div')`
  display: flex;
  flex-direction: column;
  width: 49%;
`;

const NewCalendarButton = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 400;
  width: 100%;
  height: 32px;
  padding: 6px 16px 6px 16px;
  background: #444444;
  color: white;
  border-radius: 4px;
  margin-top: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 700;
  cursor: pointer;
  user-select: none;
  border: 1px solid #444444;
`;

const ManageCalendarsButton = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 400;
  width: 100%;
  height: 32px;
  padding: 6px 16px 6px 16px;
  color: #444444;
  border-radius: 4px;
  margin-top: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 700;
  cursor: pointer;
  user-select: none;
  border: 1px solid #444444;
`;

const DaySelectors = styled('div')`
  display: flex;
  gap: 12px;
  margin-top: 16px;
`;

const DaySelector = styled('div')`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  background-color: ${(props) => (props.selected ? '#444444' : 'white')};
  color: ${(props) => (props.selected ? 'white' : '#444444')};
  border: 1px solid ${(props) => (props.selected ? '#444444' : '#E0E0E0')};
  transition: all 0.2s ease;

  &:hover {
    border-color: #444444;
  }
`;

const SelectDaysLabel = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 700;
  margin-top: 15px;
  color: black;
`;

const SetRecurringItemModal = ({
  closeModal,
}) => {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  // String versions for TBDatePicker
  const [startDateString, setStartDateString] = useState('');
  const [endDateString, setEndDateString] = useState('');
  const [selectedCadence, setSelectedCadence] = useState({});
  const [selectedPosition, setSelectedPosition] = useState({});
  const [selectedDays, setSelectedDays] = useState({
    sunday: false,
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: false,
  });

  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const modalData = useSelector((state) => state.calendar.data.modalData);
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);

  // Add useEffect to handle populating form fields from schedule data
  const leftSelectOptions = [
    {
      name: 'Every Day',
      id: 0,
    },
    {
      name: 'Weekdays',
      id: 1,
    },
    {
      name: 'Days with Scheduled Workouts',
      id: 2,
    },
    {
      name: 'Custom',
      id: 3,
    },
  ];

  const rightSelectOptions = [
    {
      name: 'Before Workout',
      id: 0,
    },
    {
      name: 'After Workout',
      id: 1,
    },
  ];

  useEffect(() => {
    if (modalData?.schedule) {
      // Set cadence data
      const cadenceOption = leftSelectOptions.find(
        (option) => option.id === modalData.schedule.cadence,
      );
      if (cadenceOption) {
        setSelectedCadence(cadenceOption);
      }

      // Set position data
      const positionOption = rightSelectOptions.find(
        (option) => option.id === modalData.schedule.position,
      );
      if (positionOption) {
        setSelectedPosition(positionOption);
      }

      // Set start date
      if (modalData.schedule.startDate) {
        const startDateObj = new Date(modalData.schedule.startDate);
        setStartDate(startDateObj);
        setStartDateString(moment(startDateObj).format('YYYY-MM-DD'));
      }

      // Set end date if it exists
      if (modalData.schedule.endDate) {
        const endDateObj = new Date(modalData.schedule.endDate);
        setEndDate(endDateObj);
        setEndDateString(moment(endDateObj).format('YYYY-MM-DD'));
      }

      // Set custom days if they exist and cadence is Custom (3)
      if (modalData.schedule.customDays && modalData.schedule.cadence === 3) {
        const dayMap = {
          0: 'sunday',
          1: 'monday',
          2: 'tuesday',
          3: 'wednesday',
          4: 'thursday',
          5: 'friday',
          6: 'saturday',
        };

        const newSelectedDays = {
          sunday: false,
          monday: false,
          tuesday: false,
          wednesday: false,
          thursday: false,
          friday: false,
          saturday: false,
        };

        modalData.schedule.customDays.forEach((dayIndex) => {
          if (dayMap[dayIndex]) {
            newSelectedDays[dayMap[dayIndex]] = true;
          }
        });

        setSelectedDays(newSelectedDays);
      }
    } else {
      // Set defaults when no existing schedule data
      setSelectedCadence(leftSelectOptions[0]); // 'Every Day'
      setSelectedPosition(rightSelectOptions[0]); // 'Before Workout'

      // Set default dates to selectedDate
      if (selectedDate) {
        const selectedDateObj = new Date(selectedDate);
        setStartDate(selectedDateObj);
        setStartDateString(moment(selectedDateObj).format('YYYY-MM-DD'));
      }
    }
  }, [modalData]);

  const dispatch = useDispatch();

  // Date picker handlers that update both formats
  const handleStartDateChange = (date) => {
    const momentDate = moment(date);
    setStartDate(momentDate.toDate());
    setStartDateString(momentDate.format('YYYY-MM-DD'));

    // Clear end date if start date is after current end date
    if (endDate && momentDate.isAfter(moment(endDate))) {
      setEndDate(null);
      setEndDateString('');
    }
  };

  const handleEndDateChange = (date) => {
    const momentDate = moment(date);
    setEndDate(momentDate.toDate());
    setEndDateString(momentDate.format('YYYY-MM-DD'));
  };

  const formatDate = () => `${moment(selectedDate).format('YYYY-MM-DD')}`;
  const formatRangeDate = (rangeDate) => `${moment(rangeDate).format('YYYY-MM-DD')}`;

  // Helper function to toggle day selection
  const toggleDay = (day) => {
    setSelectedDays({
      ...selectedDays,
      [day]: !selectedDays[day],
    });
  };

  const handleSave = () => {
    // Validate required fields
    if (!startDate) {
      toast.error('Please select a start date');
      return;
    }
    if (!selectedCadence || (!selectedCadence.id && selectedCadence.id !== 0)) {
      toast.error('Please select a scheduling option');
      return;
    }
    if (!selectedPosition || (!selectedPosition.id && selectedPosition.id !== 0)) {
      toast.error('Please select a position option');
      return;
    }
    // Continue with the existing logic
    if (!modalData.preSave && !modalData.isRecurring) {
      const updateItem = {
        cadence: selectedCadence.id,
        position: selectedPosition.id,
        startDate: formatRangeDate(startDate),
        endDate: endDate ? formatRangeDate(endDate) : null,
      };

      // Add customDays if cadence is Custom (3)
      if (selectedCadence.id === 3) {
        const dayMap = {
          sunday: 0,
          monday: 1,
          tuesday: 2,
          wednesday: 3,
          thursday: 4,
          friday: 5,
          saturday: 6,
        };

        updateItem.customDays = Object.entries(selectedDays)
          .filter(([, isSelected]) => isSelected)
          .map(([day]) => dayMap[day]);
      }
      dispatch(convertRecurringItem(
        currentUser.accountCode,
        updateItem,
        modalData.assignedId,
        modalData.itemIndex,
        formatDate(),
      ));
      closeModal();
    } else if (!modalData.preSave && modalData.isRecurring) {
      const updateItem = {
        cadence: selectedCadence.id,
        position: selectedPosition.id,
        startDate: formatRangeDate(startDate),
        endDate: endDate ? formatRangeDate(endDate) : null,
      };

      // Add customDays if cadence is Custom (3)
      if (selectedCadence.id === 3) {
        const dayMap = {
          sunday: 0,
          monday: 1,
          tuesday: 2,
          wednesday: 3,
          thursday: 4,
          friday: 5,
          saturday: 6,
        };

        updateItem.customDays = Object.entries(selectedDays)
          .filter(([, isSelected]) => isSelected)
          .map(([day]) => dayMap[day]);
      }
      dispatch(updateRecurringItemSchedule(
        currentUser.accountCode,
        updateItem,
        modalData.assignedId,
        modalData.itemIndex,
        formatDate(),
        modalData.isRecurring,
      ));
    } else {
      closeModal();
    }
  };

  const customStyles = {
    option: (provided, state) => ({
      ...provided,
      paddingLeft: state.data.parentId ? 35 : 15,
    }),
    container: (provided) => ({
      ...provided,
      width: '57%',
    }),
  };

  const customStyles2 = {
    option: (provided, state) => ({
      ...provided,
      paddingLeft: state.data.parentId ? 35 : 15,
    }),
    container: (provided) => ({
      ...provided,
      width: '41%',
    }),
  };

  return (
    <ModalContainer>
      <div className='remove-div'>
        <div style={{
          display: 'flex',
          alignItems: 'center',
        }}
        >
          <TextContainer>
            <TopTextAndX>
              <ModalTextTop>Recurring Settings</ModalTextTop>
              <div
                className='remove-div-icon'
                onClick={() => closeModal()}
                style={{
                  cursor: 'pointer',
                }}
              >
                <IcomoonReact
                  iconSet={iconSet}
                  size={15}
                  icon='remove'
                  color='grey'
                />
              </div>
            </TopTextAndX>
            <Subtext>
              All changes made to a recurring item will be reflected on every day
              that it is scheduled
              (including past instances).
              {' '}
              <div
                style={{
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  color: '#007FAE',
                }}
                onClick={() => window.open('https://support.teambuildr.com/article/t5talti5mh-recurring-workout-items', '_blank')}
              >
                Click here to learn more.
              </div>
            </Subtext>
          </TextContainer>
        </div>
      </div>
      <SchedulingOptionsStack>
        <Subtext2>
          Scheduling Options
        </Subtext2>
        <TopSelectRow>
          <Select
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option.id}
            options={leftSelectOptions}
            onChange={(option) => {
              setSelectedCadence(option);
            }}
            value={selectedCadence}
            isSearchable
            styles={customStyles}
            placeholder='Select an option'
            autoFocus
          />
          <Select
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option.id}
            options={rightSelectOptions}
            onChange={(option) => {
              setSelectedPosition(option);
            }}
            value={selectedPosition}
            isSearchable
            styles={customStyles2}
            placeholder='Select an option'
          />
        </TopSelectRow>
      </SchedulingOptionsStack>
      <DatePickerStack>
        <DatePickerRow>
          <PickerAndText>
            <Subtext2>
              Start Date
            </Subtext2>
            <TBDatePicker
              activeDate={startDateString}
              setActiveDate={handleStartDateChange}
              additionalStyles={{
                margin: '0',
                maxWidth: '100%',
              }}
              showDropShadow={false}
              maxDate={null}
              showIcon={false}
            />
          </PickerAndText>
          <PickerAndText>
            <Subtext2>
              End Date
            </Subtext2>
            <TBDatePicker
              activeDate={endDateString}
              setActiveDate={handleEndDateChange}
              additionalStyles={{
                margin: '0',
                maxWidth: '100%',
              }}
              showDropShadow={false}
              minDate={startDate}
              maxDate={null}
              showIcon={false}
            />
          </PickerAndText>
        </DatePickerRow>
      </DatePickerStack>
      {/* Show day selection when Custom cadence is selected */}
      {selectedCadence.id === 3 && (
        <div>
          <SelectDaysLabel>Select Days</SelectDaysLabel>
          <DaySelectors>
            <DaySelector
              selected={selectedDays.sunday}
              onClick={() => toggleDay('sunday')}
            >
              Su
            </DaySelector>
            <DaySelector
              selected={selectedDays.monday}
              onClick={() => toggleDay('monday')}
            >
              M
            </DaySelector>
            <DaySelector
              selected={selectedDays.tuesday}
              onClick={() => toggleDay('tuesday')}
            >
              T
            </DaySelector>
            <DaySelector
              selected={selectedDays.wednesday}
              onClick={() => toggleDay('wednesday')}
            >
              W
            </DaySelector>
            <DaySelector
              selected={selectedDays.thursday}
              onClick={() => toggleDay('thursday')}
            >
              Th
            </DaySelector>
            <DaySelector
              selected={selectedDays.friday}
              onClick={() => toggleDay('friday')}
            >
              F
            </DaySelector>
            <DaySelector
              selected={selectedDays.saturday}
              onClick={() => toggleDay('saturday')}
            >
              Sa
            </DaySelector>
          </DaySelectors>
        </div>
      )}
      <ButtonsContainer>
        <NewCalendarButton onClick={() => {
          handleSave();
        }}
        >
          SAVE
        </NewCalendarButton>
        <ManageCalendarsButton onClick={() => closeModal()}>
          CANCEL
        </ManageCalendarsButton>
      </ButtonsContainer>
    </ModalContainer>
  );
};

SetRecurringItemModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
};

export default SetRecurringItemModal;
